import { IntegrityService } from './../../core/service/integrity.service';
import { IntegrityListService } from './../../core/service/grid-data/grid-data-integrity-batch.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { IntegrityEdit } from '../model/integrity-edit';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { Subscription } from 'rxjs';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';

@Component({
  selector: 'app-integrity-list',
  templateUrl: './integrity-list.component.html',
  styleUrls: ['./integrity-list.component.scss']
})
export class IntegrityListComponent implements OnInit, OnDestroy
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  public showItemsTab = false;
  public patientLabel = {};
  public selectedBatch: IntegrityEdit;
  public editDataItem: IntegrityEdit;
  public deleteDialogOpened = false;
  public processDialogOpened = false;
  public isNew = false;
  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public integrityStages = [];
  public integrityTypes = [];
  public filterText = '';
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'lastModifiedOn', dir: 'desc' }
    ]
  };
  public selectedRecord: number[] = [];
  public maxTextLength = 200;

  private listSubscription: Subscription;

  constructor(
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private messageHandlerService: MessageHandlerService,
    private integrityService: IntegrityService,
    private settingService: SettingService,
    private integrityListService: IntegrityListService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });
  }

  public get loading(): boolean
  {
    return this.integrityListService ? this.integrityListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.integrityStages = this.integrityService.integrityStages;
    this.integrityTypes = this.integrityService.integrityTypes;
    this.listSubscription = this.integrityListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            if (d.lastModifiedOn !== null)
            {
              d.lastModifiedOn = new Date(d.lastModifiedOn);
            }
            d.integrityTypeName = this.integrityService.getIntegrityTypeText(d.integrityType);
            d.integrityStageName = this.integrityService.getIntegrityStageText(d.integrityStage);
          });
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.count = value.total : this.count = 0
        };
      }
    });
    this.getIntegrityBatches();
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new IntegrityEdit();
    });
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.integrityService.getIntegrityBatchById(id).subscribe({
      next: (data) =>
      {
        if (data.locked && data.integrityStage !== 'COMPLETED')
        {
          this.messageHandlerService.displayWarning('BATCH-LOCKED');
          return;
        }
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.integrityService.getIntegrityBatchById(id).subscribe({
      next: (data) =>
      {
        // If locked we don't allow deletion except if it is completed stage
        if (data.locked && data.integrityStage !== 'COMPLETED')
        {
          this.messageHandlerService.displayWarning('INTEGRITY-LOCKED-CANNOT-DELETE');
        }
        else
        {
          this.selectedBatch = data as IntegrityEdit;
          this.open('deleteDialog');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Process the integrity batch.
   *
   * @param id Integrity batch id.
   */
  public processHandler(id: number): void
  {
    this.integrityService.getIntegrityBatchById(id).subscribe({
      next: (data: any) =>
      {
        if (data.integrityStage === 'IN_REVIEW')
        {
          this.messageHandlerService.displayWarning('INTEGRITY-BATCH-IN-REVIEW');
        }
        // If locked we don't allow deletion except if it is completed stage
        else if (data.locked)
        {
          const messageKey = data.integrityStage === 'COMPLETED' ? 'BATCH-COMPLETED' : 'BATCH-LOCKED';
          this.messageHandlerService.displayWarning(messageKey);
        }
        else
        {
          this.selectedBatch = data as IntegrityEdit;
          this.selectedBatch.integrityTypeName = this.integrityService.getIntegrityTypeText(data.integrityType);
          this.open('processDialog');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.integrityListService.queryByState(state);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'comments');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.executeQuery();
  }

  /**
   * Save handler.
   *
   * @param integrity Object to save.
   */
  public saveChangesHandler(integrityEdit: IntegrityEdit): void
  {
    // Set UTC dates
    if (integrityEdit.fromDateSelection) 
    {
      integrityEdit.fromDateSelection = new Date(integrityEdit.fromDateSelection.getFullYear(),
        integrityEdit.fromDateSelection.getMonth(),
        integrityEdit.fromDateSelection.getDate());
    }

    if (integrityEdit.toDateSelection) 
    {
      integrityEdit.toDateSelection = new Date(integrityEdit.toDateSelection.getFullYear(),
        integrityEdit.toDateSelection.getMonth(),
        integrityEdit.toDateSelection.getDate(), 23, 59, 59, 999);
    }

    this.integrityService.saveIntegrityBatch(integrityEdit, this.isNew, integrityEdit.id).subscribe({
      next: () =>
      {
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('INTEGRITY-BATCH-SAVED-SUCCESSFULLY');
        this.getIntegrityBatches();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Cancel changes.
   */
  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Handles the row selection change of the Kendo grid.
   *
   * @param selections Kendo grid selected keys.
   */
  public onSelectedKeysChange(selections: Array<number>): void
  {
    if (selections.length > 0)
    {
      const id = selections[0]; // we only allow single selection
      const selectedItem = this.selectedBatch = this.gridView.data.find(x => x.id === id);
      this.selectedBatch = selectedItem !== undefined ? selectedItem : null;
    }
    else // if the row is unselected.
    {
      this.selectedBatch = null;
    }
    this.showItemsTab = this.selectedBatch !== null;
  }

  /**
   * Select tab on batch grid double click
   *
   * @param event MouseEvent
   *
   */
  public dblClickBatchEvent(event: any): void
  {
    const rowIndex = event.target.parentElement.rowIndex;
    if (typeof rowIndex === 'number' && rowIndex < this.gridView.data.length)
    {
      if (this.showItemsTab)
      {
        this.tabstrip.selectTab(1);
      }
    }
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      this.integrityService.deleteIntegrityBatch(this.selectedBatch.id).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('INTEGRITY-BATCH-DELETED-SUCCESSFULLY');
          this.getIntegrityBatches();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.close('deleteDialog');
  }

  public processAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the import
      this.integrityService.processIntegrityBatch(this.selectedBatch.id).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('BATCH-PROCESSED-SUCCESSFULLY');
          this.selectedBatch = null;
          this.getIntegrityBatches();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    this.close('processDialog');
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
  * Perform grid refresh.
  */
  public refresh(): void
  {
    this.getIntegrityBatches();
  }

  /**
   * Get integority batches from API per the applied filter.
   *
   * @param filter Task filter string.
   */
  private getIntegrityBatches(): void
  {
    this.state = {
      filter: {
        filters: [
          { field: 'deleted', operator: 'eq', value: false }
        ], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'lastModifiedOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };
    this.executeQuery();
  }

  /**
   * Execute integrity list query
   */
  private executeQuery(): void
  {
    this.selectedBatch = null;
    this.selectedRecord = [];
    this.showItemsTab = false;
    this.integrityListService.queryByState(this.state);
  }
}
